import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { cellsApi, getErrorMessage, type CreateCellInput } from '../lib/api';
import { fetchAllPages } from '../lib/pagination';
import type { Cell, PaginatedResponse } from '../lib/types';

export function useCells(params?: {
  batch_id?: string;
  received_box?: string;
  status?: string;
  search?: string;
  limit?: number;
  lastKey?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  fetchAll?: boolean; // If true, fetches all pages (default: false to prevent excessive API calls)
}) {
  return useQuery<PaginatedResponse<Cell>>({
    queryKey: ['cells', params],
    queryFn: async () => {
      if (params?.fetchAll === true) {
        // Only fetch all pages if explicitly requested
        const allItems = await fetchAllPages<Cell>(
          (pageParams) => cellsApi.list({ ...params, ...pageParams }),
          { limit: params?.limit || 500, maxPages: 20 }
        );
        return { items: allItems, lastKey: null };
      } else {
        // Fetch single page by default
        return cellsApi.list(params);
      }
    },
  });
}

export function useCell(id: string) {
  return useQuery<Cell>({
    queryKey: ['cell', id],
    queryFn: () => cellsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCell() {
  const queryClient = useQueryClient();

  return useMutation<Cell, Error, CreateCellInput>({
    mutationFn: cellsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Cell created successfully');
    },
    onError: (error) => {
      toast.error(`Error creating cell: ${getErrorMessage(error)}`);
    },
  });
}

export function useDeleteCell() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: cellsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Cell deleted successfully');
    },
    onError: (error) => {
      toast.error(`Error deleting cell: ${getErrorMessage(error)}`);
    },
  });
}

export function useUpdateCell() {
  const queryClient = useQueryClient();

  return useMutation<Cell, Error, { id: string; data: Partial<CreateCellInput> }>({
    mutationFn: ({ id, data }) => cellsApi.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      queryClient.invalidateQueries({ queryKey: ['cell', data.cell_id] });
      toast.success('Cell updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating cell: ${getErrorMessage(error)}`);
    },
  });
}
